import React, { useState } from "react";
import PropTypes from "prop-types";
import { HelpCircle } from "lucide-react";
import { meterpointService } from "../../../../services/meterpoint";
import MeterNumberInput from "./MeterNumberInput";
import {
  validateAddressSelection,
  validateManualAddress,
  validateMeterType,
  validateMeterNumber,
  formatAddress,
  getSelectPlaceholderText,
  extractMeterPointDetails,
} from "./helpers";
import styles from "./AddressSelection.module.css";
/* global globalThis */

const getGlobalObject = () => {
  return typeof globalThis === "undefined" ? {} : globalThis;
};

const reportError = (err) => {
  const sentry = getGlobalObject().Sentry;
  if (sentry?.captureException) {
    sentry.captureException(err);
  }
};

const AddressDropdown = ({
  selectedAddress,
  onAddressSelection,
  addressSelectionError,
  isLoadingAddresses,
  addressData,
  availableAddresses,
  showManualAddress,
}) => (
  <div className={styles.addressDropdownContainer}>
    <div className={styles.inputWithIcon}>
      {isLoadingAddresses && (
        <output
          className={styles.loadingSpinner}
          aria-label="Loading addresses"
        ></output>
      )}
      <select
        id="address-select"
        value={selectedAddress}
        onChange={(e) => onAddressSelection(e.target.value)}
        className={`${styles.addressDropdown} ${addressSelectionError ? styles.inputError : ""} ${isLoadingAddresses ? styles.loading : ""}`}
        disabled={
          addressData?.length === 0 ||
          isLoadingAddresses ||
          availableAddresses == null ||
          showManualAddress
        }
        aria-invalid={!!addressSelectionError}
        aria-describedby={addressSelectionError ? "address-error" : undefined}
        aria-labelledby="address-selection-heading"
      >
        <option value="">
          {getSelectPlaceholderText(isLoadingAddresses, availableAddresses)}
        </option>
        {!isLoadingAddresses &&
          availableAddresses?.map((address, index) => (
            <option
              key={`address-${index}-${address.substring(0, 20)}`}
              value={address}
              title={address}
            >
              {address.length > 60 ? address.substring(0, 60) + "..." : address}
            </option>
          ))}
      </select>
    </div>
    {addressSelectionError && (
      <div
        id="address-error"
        className={styles.errorMessage}
        role="alert"
        aria-live="polite"
      >
        {addressSelectionError}
      </div>
    )}
  </div>
);

AddressDropdown.propTypes = {
  selectedAddress: PropTypes.string.isRequired,
  onAddressSelection: PropTypes.func.isRequired,
  addressSelectionError: PropTypes.string,
  isLoadingAddresses: PropTypes.bool,
  addressData: PropTypes.arrayOf(PropTypes.object),
  availableAddresses: PropTypes.arrayOf(PropTypes.string),
  showManualAddress: PropTypes.bool,
};

const FormInput = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  errorId,
  autoComplete,
}) => (
  <div className={styles.formGroup}>
    <label htmlFor={id} className={styles.formLabel}>
      {label}
    </label>
    <div className={styles.inputWithIcon}>
      <input
        id={id}
        type="text"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        className={`${styles.formInput} ${error ? styles.inputError : ""}`}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        autoComplete={autoComplete}
      />
      {error && (
        <div className={styles.errorIcon} aria-hidden="true">
          !
        </div>
      )}
    </div>
    {error && (
      <div
        id={errorId}
        className={styles.errorMessage}
        role="alert"
        aria-live="polite"
      >
        {error}
      </div>
    )}
  </div>
);

FormInput.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  error: PropTypes.string,
  errorId: PropTypes.string.isRequired,
  autoComplete: PropTypes.string,
};

const SimpleFormInput = ({ id, label, value, onChange }) => (
  <div className={styles.formGroup}>
    <label htmlFor={id} className={styles.formLabel}>
      {label}
    </label>
    <input
      id={id}
      type="text"
      value={value}
      onChange={onChange}
      className={styles.formInput}
    />
  </div>
);

SimpleFormInput.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

const MeterTypeSelect = ({
  value,
  onChange,
  onBlur,
  hasError,
  errorMessage,
}) => (
  <div className={styles.formGroup}>
    <label htmlFor="meter-type" className={styles.formLabel}>
      Meter type
    </label>
    <div className={styles.inputWithIcon}>
      <select
        id="meter-type"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        className={`${styles.formSelect} ${hasError ? styles.inputError : ""}`}
        aria-invalid={!!hasError}
        aria-describedby={hasError ? "meter-type-error" : undefined}
      >
        <option value=""></option>
        <option value="credit">Credit</option>
        <option value="prepayment">Prepayment</option>
      </select>
    </div>
    {hasError && (
      <div
        id="meter-type-error"
        className={styles.errorMessage}
        role="alert"
        aria-live="polite"
      >
        {errorMessage}
      </div>
    )}
  </div>
);

MeterTypeSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
};

const SingleRateBillingCheckbox = ({
  checked,
  onChange,
  showHelp,
  onToggleHelp,
}) => (
  <div className={styles.formGroup}>
    <div className={styles.labelSection}>
      <label className={styles.checkboxLabel}>
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className={styles.checkbox}
        />
        <span className={styles.checkboxText}>Single-rate billing</span>
        <button
          type="button"
          className={styles.helpIcon}
          onClick={onToggleHelp}
          aria-label={`${showHelp ? "Hide" : "Show"} single-rate billing help information`}
          aria-expanded={showHelp}
          aria-controls="single-rate-help"
        >
          <HelpCircle size={20} />
        </button>
      </label>
      {showHelp && (
        <section
          id="single-rate-help"
          className={styles.infoBox}
          aria-label="Single-rate billing help information"
        >
          Single-rate billing applies a single rate for all electricity usage,
          regardless of time of day.
        </section>
      )}
    </div>
  </div>
);

SingleRateBillingCheckbox.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  showHelp: PropTypes.bool,
  onToggleHelp: PropTypes.func.isRequired,
};

const ManualAddressInstructions = () => (
  <div className="row">
    <div className="col-xs-12">
      <p className={styles.instructionText}>
        Please accurately enter your address details below. You can find your
        meter number on your latest bill or statement.
      </p>
      <p className={styles.instructionText}>
        If you don&apos;t have this information, please call us on{" "}
        <strong>
          <a href="tel:0333 666 0276" className={styles.phoneLink}>
            0333 666 0276
          </a>
        </strong>
        {". "}Lines open Mon – Thu 8.30am – 5.00pm and Fri 8.30am – 4.30pm.
      </p>
    </div>
  </div>
);

const AddressSelection = React.forwardRef(
  (
    {
      showManualAddress,
      onToggleManualAddress,
      manualAddress,
      onManualAddressChange,
      addressData = [],
      onAddressSelect,
      postcode,
      isLoadingAddresses = false,
      selectedFuel = "electricity",
      selectedAddress: propSelectedAddress = "",
      onLookup,
    },
    ref,
  ) => {
    const [postcodeError, setPostcodeError] = useState("");
    const [addressLine1Error, setAddressLine1Error] = useState("");
    const [cityError, setCityError] = useState("");
    const [meterTypeError, setMeterTypeError] = useState("");
    const [addressSelectionError, setAddressSelectionError] = useState("");
    const [showMeterHelp, setShowMeterHelp] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState("");
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [meterNumberError, setMeterNumberError] = useState("");
    const [meterTypeDirty, setMeterTypeDirty] = useState(false);
    const [detectedMeterType, setDetectedMeterType] = useState("");
    const [singleRateBilling, setSingleRateBilling] = useState(false);
    const [showSingleRateHelp, setShowSingleRateHelp] = useState(false);
    const [meterValidated, setMeterValidated] = useState(false);

    React.useEffect(() => {
      setSelectedAddress(propSelectedAddress);
    }, [propSelectedAddress, selectedFuel]);

    const availableAddresses = addressData?.map(formatAddress);

    const validateManualAddressForm = () => {
      const errors = validateManualAddress(manualAddress);
      setAddressLine1Error(errors.addressLine1);
      setCityError(errors.city);
      setPostcodeError(errors.postcode);

      let isValid = !(errors.addressLine1 || errors.city || errors.postcode);

      const meterError = validateMeterType(manualAddress.meterType);
      setMeterTypeError(meterError);
      if (meterError) {
        isValid = false;
      }

      if (selectedFuel === "electricity") {
        const meterNumError = validateMeterNumber(meterValidated);
        setMeterNumberError(meterNumError);
        if (meterNumError) {
          isValid = false;
        }
      }

      return isValid;
    };

    const validateAddressDropdown = () => {
      const error = validateAddressSelection(selectedAddress);
      setAddressSelectionError(error);
      return !error;
    };

    const validateForm = () => {
      setHasSubmitted(true);

      if (showManualAddress) {
        setAddressSelectionError("");
        return validateManualAddressForm();
      } else {
        return validateAddressDropdown();
      }
    };

    React.useImperativeHandle(ref, () => ({
      validate: validateForm,
    }));

    const handleAddressLine1Change = (value) => {
      setAddressLine1Error(
        value.trim() === "" ? "Please enter your address" : "",
      );
    };

    const handleCityChange = (value) => {
      setCityError(value.trim() === "" ? "Please enter your city" : "");
    };

    const handlePostcodeChange = (value) => {
      if (value.trim() === "") {
        setPostcodeError("Please enter your postcode");
      } else {
        const errors = validateManualAddress({
          ...manualAddress,
          postcode: value,
        });
        setPostcodeError(errors.postcode);
      }
    };

    const handleMeterTypeChange = (value) => {
      setMeterTypeDirty(true);
      if (hasSubmitted && value) {
        setMeterTypeError("");
      }
    };

    const fieldChangeHandlers = {
      addressLine1: handleAddressLine1Change,
      city: handleCityChange,
      postcode: handlePostcodeChange,
      meterType: handleMeterTypeChange,
    };

    const handleInputChange = (field, value) => {
      onManualAddressChange(field, value);
      const handler = fieldChangeHandlers[field];
      if (handler) {
        handler(value);
      }
    };

    const processMeterValidation = async (
      meterDetails,
      selectedAddressObj,
      address,
    ) => {
      try {
        const mpanCode = `${meterDetails.profileClassId}${meterDetails.meterTimeswitchCode}${meterDetails.lineLossFactorClassId}`;
        const response = await meterpointService.validateMpan(mpanCode);
        const meterType = response.mpanType || response.MpanType;
        setDetectedMeterType(meterType);

        if (onAddressSelect) {
          onAddressSelect(selectedAddressObj, address, meterType);
        }
      } catch (error) {
        reportError(error);
        if (onAddressSelect) {
          onAddressSelect(selectedAddressObj, address, "");
        }
      }
    };

    const handleAddressSelection = async (address) => {
      setSelectedAddress(address);

      const selectedAddressObj = addressData.find(
        (addr) => formatAddress(addr) === address,
      );

      const clearErrorIfNeeded = () => {
        if (hasSubmitted && address) setAddressSelectionError("");
      };

      const toggleManualIfNeeded = () => {
        if (showManualAddress) onToggleManualAddress(false);
      };

      if (!selectedAddressObj) {
        if (onAddressSelect) onAddressSelect(null, address, "");
        clearErrorIfNeeded();
        toggleManualIfNeeded();
        return;
      }

      const meterDetails = extractMeterPointDetails(selectedAddressObj);

      if (meterDetails.hasAllDetails) {
        await processMeterValidation(meterDetails, selectedAddressObj, address);
      } else if (onAddressSelect) {
        onAddressSelect(selectedAddressObj, address, "");
      }

      clearErrorIfNeeded();
      toggleManualIfNeeded();
    };

    const performAddressLookup = async () => {
      if (!onLookup || !postcode) return;
      onLookup([], true);
      try {
        const results = await meterpointService.lookupByPostcode(
          postcode,
          selectedFuel,
        );
        onLookup(results, false);
      } catch (error) {
        reportError(error);
        onLookup([], false);
      }
    };

    const handleManualAddressToggle = async (isChecked) => {
      if (isChecked) {
        setSelectedAddress("");
        onToggleManualAddress(isChecked);
      } else {
        onToggleManualAddress(isChecked);
        await performAddressLookup();
      }
    };

    const handleAddressLine1Blur = () => {
      if (
        !manualAddress.addressLine1 ||
        manualAddress.addressLine1.trim() === ""
      ) {
        setAddressLine1Error("Please enter your address");
      }
    };

    const handleCityBlur = () => {
      if (!manualAddress.city || manualAddress.city.trim() === "") {
        setCityError("Please enter your city");
      }
    };

    const handlePostcodeBlur = () => {
      if (!manualAddress.postcode || manualAddress.postcode.trim() === "") {
        setPostcodeError("Please enter your postcode");
      } else {
        const errors = validateManualAddress(manualAddress);
        setPostcodeError(errors.postcode);
      }
    };

    const meterTypeHasError =
      (meterTypeDirty && !manualAddress.meterType) ||
      (hasSubmitted && meterTypeError);

    return (
      <div className={styles.container}>
        <h3 id="address-selection-heading" className={styles.title}>
          Select your business site address
        </h3>

        <div>
          <AddressDropdown
            selectedAddress={selectedAddress}
            onAddressSelection={handleAddressSelection}
            addressSelectionError={addressSelectionError}
            isLoadingAddresses={isLoadingAddresses}
            addressData={addressData}
            availableAddresses={availableAddresses}
            showManualAddress={showManualAddress}
          />
        </div>

        {availableAddresses && availableAddresses?.length > 0 && (
          <div className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={showManualAddress}
              onChange={(e) => handleManualAddressToggle(e.target.checked)}
              className={styles.checkbox}
            />
            <span className={styles.checkboxText}>
              My address isn&apos;t listed
            </span>
          </div>
        )}

        {showManualAddress && (
          <div className={styles.manualAddressForm}>
            <ManualAddressInstructions />

            <FormInput
              id="address-line-1"
              label="Address line 1"
              value={manualAddress.addressLine1}
              onChange={(e) =>
                handleInputChange("addressLine1", e.target.value)
              }
              onBlur={handleAddressLine1Blur}
              error={addressLine1Error}
              errorId="address-line-1-error"
            />

            <SimpleFormInput
              id="address-line-2"
              label="Address line 2"
              value={manualAddress.addressLine2}
              onChange={(e) =>
                handleInputChange("addressLine2", e.target.value)
              }
            />

            <FormInput
              id="city"
              label="City"
              value={manualAddress.city}
              onChange={(e) => handleInputChange("city", e.target.value)}
              onBlur={handleCityBlur}
              error={cityError}
              errorId="city-error"
            />

            <FormInput
              id="manual-postcode"
              label="Postcode"
              value={manualAddress.postcode}
              onChange={(e) => handleInputChange("postcode", e.target.value)}
              onBlur={handlePostcodeBlur}
              error={postcodeError}
              errorId="manual-postcode-error"
              autoComplete="postal-code"
            />

            <MeterNumberInput
              selectedFuel={selectedFuel}
              meterNumber={manualAddress.meterNumber}
              onMeterNumberChange={(value) =>
                handleInputChange("meterNumber", value)
              }
              showMeterHelp={showMeterHelp}
              setShowMeterHelp={setShowMeterHelp}
              meterNumberError={meterNumberError}
              setMeterNumberError={setMeterNumberError}
              meterValidated={meterValidated}
              setMeterValidated={setMeterValidated}
              onManualAddressChange={onManualAddressChange}
            />

            <MeterTypeSelect
              value={manualAddress.meterType}
              onChange={(e) => handleInputChange("meterType", e.target.value)}
              onBlur={() => setMeterTypeDirty(true)}
              hasError={meterTypeHasError}
              errorMessage={meterTypeError}
            />

            {detectedMeterType === "Day/Night" && (
              <SingleRateBillingCheckbox
                checked={singleRateBilling}
                onChange={(e) => setSingleRateBilling(e.target.checked)}
                showHelp={showSingleRateHelp}
                onToggleHelp={() => setShowSingleRateHelp(!showSingleRateHelp)}
              />
            )}
          </div>
        )}
      </div>
    );
  },
);

AddressSelection.propTypes = {
  showManualAddress: PropTypes.bool.isRequired,
  onToggleManualAddress: PropTypes.func.isRequired,
  manualAddress: PropTypes.shape({
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    city: PropTypes.string,
    postcode: PropTypes.string,
    meterNumber: PropTypes.string,
    meterType: PropTypes.string,
    mpanCore: PropTypes.string,
  }).isRequired,
  onManualAddressChange: PropTypes.func.isRequired,
  addressData: PropTypes.arrayOf(PropTypes.object),
  onAddressSelect: PropTypes.func,
  postcode: PropTypes.string.isRequired,
  isLoadingAddresses: PropTypes.bool,
  selectedFuel: PropTypes.string,
  selectedAddress: PropTypes.string,
  onLookup: PropTypes.func,
};

AddressSelection.displayName = "AddressSelection";

export default AddressSelection;
